import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, RefreshCw, Sparkles, TrendingUp, AlertCircle, CheckCircle2, Loader2, Mail, Calendar, Target, Gift, RotateCw, Palette } from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint 1 — Mailchimp AI Style Learning
 * Lets the user sync their recent campaign history and then ask Claude to
 * extract a "brand style guide" + best-practices recommendations. The guide
 * persists in MongoDB and will be the few-shot context for Sprint 2's
 * AI Email Designer.
 */
export default function MailchimpAIStyle() {
  const [status, setStatus] = useState(null);
  const [styleGuide, setStyleGuide] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [designSystem, setDesignSystem] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [learning, setLearning] = useState(false);
  const [learningDesign, setLearningDesign] = useState(false);

  const refreshAll = async () => {
    try {
      const [st, sg, rc, ds] = await Promise.all([
        api.get("/mailchimp/history-status"),
        api.get("/mailchimp/style-guide").catch(() => ({ data: { exists: false } })),
        api.get("/mailchimp/recent-campaigns?limit=10").catch(() => ({ data: [] })),
        api.get("/mailchimp/design-system").catch(() => ({ data: { exists: false } })),
      ]);
      setStatus(st.data || null);
      setStyleGuide(sg.data?.exists ? sg.data : null);
      setTopCampaigns(rc.data || []);
      setDesignSystem(ds.data?.exists ? ds.data : null);
    } catch (e) {
      // silent
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshAll(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/mailchimp/sync-history");
      toast.success(`Sincronizadas ${r.data.synced} campañas (últimos ${r.data.window_months} meses)`);
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error sincronizando Mailchimp");
    } finally {
      setSyncing(false);
    }
  };

  const handleLearn = async () => {
    setLearning(true);
    try {
      const r = await api.post("/mailchimp/learn-style?top_n=8");
      toast.success(`Style guide v${r.data.version} generado a partir de ${r.data.campaigns_analyzed} campañas`);
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error analizando con IA");
    } finally {
      setLearning(false);
    }
  };

  const handleLearnDesign = async () => {
    setLearningDesign(true);
    try {
      const r = await api.post("/mailchimp/learn-design-system?top_n=6");
      toast.success(`Design system v${r.data.version} extraído (${r.data.design?.blocks?.length || 0} bloques)`);
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error extrayendo design system");
    } finally {
      setLearningDesign(false);
    }
  };

  const style = styleGuide?.style || null;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—";
  const fmtPct = (v) => `${((v || 0) * 100).toFixed(1)}%`;

  return (
    <div className="space-y-6" data-testid="mailchimp-ai-style">
      {/* Header / Actions */}
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#D95D39]" />
              <h3 className="font-display text-2xl text-[#0A1128]">Aprendizaje de estilo de marca</h3>
            </div>
            <p className="text-xs text-[#8A8F98] max-w-2xl">
              La IA analiza tus campañas recientes con mejor engagement (últimos 18 meses) y extrae tu voz, asuntos ganadores y oportunidades de automatización. Este &ldquo;style guide&rdquo; se usará después para que el AI Designer escriba emails que <strong>suenen a Espíritu Travel</strong>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              data-testid="mc-sync-history-btn"
              variant="outline"
              className="rounded-sm border-[#0A1128]/20 text-[#0A1128] hover:bg-[#0A1128]/5"
              onClick={handleSync}
              disabled={syncing || learning}
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sincronizar histórico
            </Button>
            <Button
              data-testid="mc-learn-style-btn"
              className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm"
              onClick={handleLearn}
              disabled={syncing || learning || learningDesign}
            >
              {learning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {learning ? "Analizando con IA…" : "Aprender / Regenerar"}
            </Button>
            <Button
              data-testid="mc-learn-design-btn"
              variant="outline"
              className="rounded-sm border-[#0A1128]/20 text-[#0A1128] hover:bg-[#0A1128]/5"
              onClick={handleLearnDesign}
              disabled={syncing || learning || learningDesign}
            >
              {learningDesign ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {learningDesign ? "Extrayendo…" : "Aprender Design System"}
            </Button>
          </div>
        </div>

        {/* Status row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[#E2DCD0]">
          <StatTile label="Campañas en DB" value={status?.total_in_db ?? "—"} />
          <StatTile label="Última sync" value={fmtDate(status?.last_synced_at)} />
          <StatTile label="Guide versión" value={status?.style_guide_version ? `v${status.style_guide_version}` : "—"} />
          <StatTile label="Campañas analizadas" value={status?.campaigns_analyzed ?? "—"} />
        </div>
      </Card>

      {/* Empty state */}
      {!style && !learning && (
        <Card className="edit-card p-8 rounded-sm shadow-none text-center" data-testid="mc-empty-state">
          <AlertCircle className="w-10 h-10 mx-auto text-[#8A8F98] mb-3" />
          <h4 className="font-display text-xl text-[#0A1128] mb-1">Sin style guide todavía</h4>
          <p className="text-sm text-[#8A8F98] max-w-md mx-auto">
              Pulsa <strong>&ldquo;Aprender / Regenerar&rdquo;</strong> para que la IA analice tus mejores campañas y construya el style guide. Tarda ~40-50 segundos.
          </p>
        </Card>
      )}

      {/* Top campaigns table */}
      {topCampaigns.length > 0 && (
        <Card className="edit-card p-6 rounded-sm shadow-none" data-testid="mc-top-campaigns">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#D95D39]" />
            <h3 className="font-display text-xl text-[#0A1128]">Top campañas por engagement</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[#8A8F98] border-b border-[#E2DCD0]">
                <tr>
                  <th className="text-left py-2 pr-3">#</th>
                  <th className="text-left py-2 pr-3">Asunto</th>
                  <th className="text-left py-2 pr-3">Audiencia</th>
                  <th className="text-right py-2 pr-3">Enviados</th>
                  <th className="text-right py-2 pr-3">Open</th>
                  <th className="text-right py-2 pr-3">Click</th>
                  <th className="text-right py-2 pr-3">Score</th>
                  <th className="text-right py-2">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {topCampaigns.map((c, i) => (
                  <tr key={c.id} className="border-b border-[#F0EBE0] last:border-0" data-testid={`mc-top-row-${i}`}>
                    <td className="py-2 pr-3 text-[#8A8F98]">{i + 1}</td>
                    <td className="py-2 pr-3 text-[#0A1128] font-medium">
                      {c.archive_url ? (
                        <a href={c.archive_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {c.subject_line || "(sin asunto)"}
                        </a>
                      ) : (c.subject_line || "(sin asunto)")}
                    </td>
                    <td className="py-2 pr-3 text-[#8A8F98]">{c.list_name || "—"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{c.recipient_count?.toLocaleString() ?? "—"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtPct(c.open_rate)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtPct(c.click_rate)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium text-[#D95D39]">{(c.engagement_score * 100).toFixed(1)}</td>
                    <td className="py-2 text-right text-xs text-[#8A8F98]">{(c.send_time || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Style Guide */}
      {style && (
        <>
          {/* Summary */}
          {style.summary && (
            <Card className="edit-card p-6 rounded-sm shadow-none bg-gradient-to-br from-[#F9F8F6] to-white" data-testid="mc-style-summary">
              <h3 className="font-display text-xl text-[#0A1128] mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#D95D39]" /> Resumen ejecutivo
              </h3>
              <p className="text-sm text-[#0A1128] leading-relaxed">{style.summary}</p>
            </Card>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Tone */}
            <Card className="edit-card p-6 rounded-sm shadow-none" data-testid="mc-style-tone">
              <h4 className="font-display text-lg text-[#0A1128] mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[#D95D39]" /> Tono & voz</h4>
              <DefItem label="Voz" value={style.tone?.voice} />
              <DefItem label="Persona" value={style.tone?.person} />
              <DefItem label="Idioma" value={style.tone?.language_default} />
              <DefItem label="Sign-off" value={style.tone?.signature_off} />
            </Card>

            {/* Subject Lines */}
            <Card className="edit-card p-6 rounded-sm shadow-none" data-testid="mc-style-subjects">
              <h4 className="font-display text-lg text-[#0A1128] mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-[#D95D39]" /> Asuntos ganadores</h4>
              <DefItem label="Longitud media" value={`${style.subject_lines?.avg_length_chars || "—"} chars`} />
              <DefItem label="Uso de emojis" value={style.subject_lines?.emoji_usage} />
              {(style.subject_lines?.winning_patterns || []).length > 0 && (
                <div className="mt-2 mb-3">
                  <div className="text-xs uppercase text-[#8A8F98] mb-1">Patrones ganadores</div>
                  <div className="flex flex-wrap gap-1.5">
                    {style.subject_lines.winning_patterns.map((p, i) => (
                      <Badge key={i} variant="outline" className="text-xs rounded-sm border-[#D95D39]/40 text-[#D95D39]">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {(style.subject_lines?.examples_high_engagement || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase text-[#8A8F98] mb-1">Ejemplos reales</div>
                  <ul className="text-sm text-[#0A1128] space-y-1">
                    {style.subject_lines.examples_high_engagement.map((s, i) => (
                      <li key={i} className="leading-snug">&ldquo;{s}&rdquo;</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>

            {/* Structure */}
            {style.structure && (
              <Card className="edit-card p-6 rounded-sm shadow-none" data-testid="mc-style-structure">
                <h4 className="font-display text-lg text-[#0A1128] mb-3 flex items-center gap-2"><RotateCw className="w-4 h-4 text-[#D95D39]" /> Estructura típica</h4>
                <DefItem label="Palabras (media)" value={style.structure.avg_word_count} />
                <DefItem label="Hero image" value={style.structure.uses_hero_image ? "Sí" : "No"} />
                {(style.structure.primary_cta_phrases || []).length > 0 && (
                  <div className="mt-2 mb-2">
                    <div className="text-xs uppercase text-[#8A8F98] mb-1">CTAs habituales</div>
                    <div className="flex flex-wrap gap-1.5">
                      {style.structure.primary_cta_phrases.map((p, i) => (
                        <Badge key={i} variant="outline" className="text-xs rounded-sm">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {(style.structure.sections_common_order || []).length > 0 && (
                  <div className="mt-2 text-xs text-[#8A8F98]">
                    <span className="uppercase">Secciones: </span>
                    <span className="text-[#0A1128]">{style.structure.sections_common_order.join(" → ")}</span>
                  </div>
                )}
              </Card>
            )}

            {/* Audience */}
            {style.audience_signals && (
              <Card className="edit-card p-6 rounded-sm shadow-none" data-testid="mc-style-audience">
                <h4 className="font-display text-lg text-[#0A1128] mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#D95D39]" /> Audiencia objetivo</h4>
                {(style.audience_signals.implied_personas || []).length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs uppercase text-[#8A8F98] mb-1">Personas implícitas</div>
                    <div className="flex flex-wrap gap-1.5">
                      {style.audience_signals.implied_personas.map((p, i) => (
                        <Badge key={i} variant="outline" className="text-xs rounded-sm">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {(style.audience_signals.destinations_most_promoted || []).length > 0 && (
                  <div>
                    <div className="text-xs uppercase text-[#8A8F98] mb-1">Destinos promocionados</div>
                    <div className="flex flex-wrap gap-1.5">
                      {style.audience_signals.destinations_most_promoted.map((p, i) => (
                        <Badge key={i} className="text-xs rounded-sm bg-[#0A1128] text-white">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Best Practices */}
          {(style.best_practices_observed?.length > 0 || style.best_practices_recommendations?.length > 0) && (
            <div className="grid lg:grid-cols-2 gap-6">
              {style.best_practices_observed?.length > 0 && (
                <Card className="edit-card p-6 rounded-sm shadow-none border-l-4 border-l-green-600" data-testid="mc-style-observed">
                  <h4 className="font-display text-lg text-[#0A1128] mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-700" /> Lo que ya haces bien
                  </h4>
                  <ul className="space-y-2">
                    {style.best_practices_observed.map((b, i) => (
                      <li key={i} className="text-sm text-[#0A1128] flex gap-2"><span className="text-green-700 shrink-0">✓</span><span>{b}</span></li>
                    ))}
                  </ul>
                </Card>
              )}
              {style.best_practices_recommendations?.length > 0 && (
                <Card className="edit-card p-6 rounded-sm shadow-none border-l-4 border-l-[#D95D39]" data-testid="mc-style-recommendations">
                  <h4 className="font-display text-lg text-[#0A1128] mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-[#D95D39]" /> Recomendaciones (IA + baseline)
                  </h4>
                  <ul className="space-y-2">
                    {style.best_practices_recommendations.map((b, i) => (
                      <li key={i} className="text-sm text-[#0A1128] flex gap-2"><span className="text-[#D95D39] shrink-0">→</span><span>{b}</span></li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          {/* Automation Opportunities */}
          {style.automation_opportunities?.length > 0 && (
            <Card className="edit-card p-6 rounded-sm shadow-none" data-testid="mc-style-automations">
              <h4 className="font-display text-lg text-[#0A1128] mb-1 flex items-center gap-2">
                <Gift className="w-4 h-4 text-[#D95D39]" /> Automatizaciones recomendadas
              </h4>
              <p className="text-xs text-[#8A8F98] mb-4">Estas se usarán como base para los flows automatizados del Sprint 3 (cumpleaños, post-trip, win-back, etc.).</p>
              <div className="grid md:grid-cols-2 gap-3">
                {style.automation_opportunities.map((a, i) => (
                  <div key={i} className="border border-[#E2DCD0] rounded-sm p-4 bg-[#F9F8F6]/50" data-testid={`mc-automation-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-3.5 h-3.5 text-[#D95D39]" />
                      <span className="font-medium text-sm uppercase text-[#0A1128]">{a.type?.replace(/_/g, " ") || "Automatización"}</span>
                    </div>
                    <div className="text-xs text-[#8A8F98] mb-2"><strong>Trigger:</strong> {a.trigger}</div>
                    <div className="text-sm text-[#0A1128] leading-snug mb-2">{a.rationale}</div>
                    {a.draft_subject && (
                      <div className="text-xs text-[#0A1128] bg-white border border-[#E2DCD0] rounded-sm p-2 mt-2">
                        <span className="text-[#8A8F98]">Asunto sugerido:</span><br />
                        &ldquo;{a.draft_subject}&rdquo;
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Design System (independent from style guide) */}
      {designSystem && designSystem.design && (
        <Card className="edit-card p-6 rounded-sm shadow-none" data-testid="mc-design-system">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl text-[#0A1128] flex items-center gap-2">
              <Palette className="w-5 h-5 text-[#D95D39]" /> Design System aprendido
            </h3>
            <Badge variant="outline" className="rounded-sm text-xs">v{designSystem.version} · {designSystem.campaigns_analyzed} campañas</Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Color palette */}
            <div>
              <h4 className="text-xs uppercase text-[#8A8F98] tracking-wider mb-2">Paleta de marca</h4>
              <div className="flex flex-wrap gap-3">
                {Object.entries(designSystem.design.design_tokens || {})
                  .filter(([k, v]) => typeof v === "string" && v.startsWith("#"))
                  .map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2" data-testid={`color-${k}`}>
                      <div className="w-8 h-8 rounded-sm border border-[#E2DCD0]" style={{ background: v }} />
                      <div className="text-xs">
                        <div className="font-medium text-[#0A1128]">{k.replace(/_/g, " ")}</div>
                        <div className="text-[#8A8F98] tabular-nums">{v}</div>
                      </div>
                    </div>
                  ))}
              </div>

              <h4 className="text-xs uppercase text-[#8A8F98] tracking-wider mt-5 mb-2">Tipografía & estructura</h4>
              <div className="text-sm text-[#0A1128] space-y-1">
                <div><span className="text-[#8A8F98] mr-2">Primaria:</span>{designSystem.design.design_tokens?.font_primary}</div>
                {designSystem.design.design_tokens?.font_secondary && (
                  <div><span className="text-[#8A8F98] mr-2">Secundaria:</span>{designSystem.design.design_tokens.font_secondary}</div>
                )}
                <div><span className="text-[#8A8F98] mr-2">Ancho wrapper:</span>{designSystem.design.design_tokens?.wrapper_max_width_px}px</div>
                <div><span className="text-[#8A8F98] mr-2">Border radius:</span>{designSystem.design.design_tokens?.border_radius_px}px</div>
              </div>

              {(designSystem.raw_tokens?.cta_text_examples?.length > 0) && (
                <>
                  <h4 className="text-xs uppercase text-[#8A8F98] tracking-wider mt-5 mb-2">CTAs detectados</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {designSystem.raw_tokens.cta_text_examples.map((c, i) => (
                      <Badge key={i} variant="outline" className="text-xs rounded-sm uppercase tracking-wider">{c}</Badge>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Blocks */}
            <div>
              <h4 className="text-xs uppercase text-[#8A8F98] tracking-wider mb-2">Bloques reutilizables ({designSystem.design.blocks?.length || 0})</h4>
              <div className="space-y-2">
                {(designSystem.design.blocks || []).map((b, i) => (
                  <div key={i} className="border border-[#E2DCD0] rounded-sm p-3 bg-[#F9F8F6]/50" data-testid={`block-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm text-[#0A1128] uppercase tracking-wider">{b.type?.replace(/_/g, " ")}</span>
                      {b.props?.length > 0 && (
                        <span className="text-xs text-[#8A8F98]">{b.props.length} props</span>
                      )}
                    </div>
                    <p className="text-xs text-[#8A8F98] leading-relaxed">{b.description}</p>
                  </div>
                ))}
              </div>

              {(designSystem.design.typical_email_order?.length > 0) && (
                <div className="mt-5">
                  <h4 className="text-xs uppercase text-[#8A8F98] tracking-wider mb-2">Orden típico</h4>
                  <p className="text-xs text-[#0A1128] leading-relaxed">
                    {designSystem.design.typical_email_order.join(" → ")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {designSystem.design.design_notes && (
            <div className="mt-5 pt-5 border-t border-[#E2DCD0]">
              <h4 className="text-xs uppercase text-[#8A8F98] tracking-wider mb-2">Notas del análisis</h4>
              <p className="text-sm text-[#0A1128] leading-relaxed">{designSystem.design.design_notes}</p>
            </div>
          )}

          {(designSystem.recent_templates?.length > 0) && (
            <div className="mt-5 pt-5 border-t border-[#E2DCD0]">
              <h4 className="text-xs uppercase text-[#8A8F98] tracking-wider mb-3">Plantillas recientes (2022+)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {designSystem.recent_templates.slice(0, 8).map((t) => (
                  <div key={t.id} className="border border-[#E2DCD0] rounded-sm overflow-hidden">
                    {t.thumbnail && <img src={t.thumbnail} alt={t.name} className="w-full h-24 object-cover" />}
                    <div className="p-2 text-xs">
                      <div className="font-medium text-[#0A1128] truncate" title={t.name}>{t.name}</div>
                      <div className="text-[#8A8F98]">{(t.date_created || "").slice(0, 10)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase text-[#8A8F98] tracking-wider">{label}</div>
      <div className="text-base font-medium text-[#0A1128] mt-0.5">{value}</div>
    </div>
  );
}

function DefItem({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="text-sm py-1">
      <span className="text-[#8A8F98] uppercase tracking-wide text-xs mr-2">{label}</span>
      <span className="text-[#0A1128]">{value}</span>
    </div>
  );
}
